import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Bell, AlertTriangle, Clock, FileWarning, RefreshCw,
  ChevronRight, Calendar, DollarSign, AlertCircle, CheckCircle,
  XCircle, Timer, FileQuestion
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RemindersPanel() {
  const [summary, setSummary] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [missingFields, setMissingFields] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, overdueRes, upcomingRes, missingRes] = await Promise.all([
        axios.get(`${API}/reminders/summary`, { withCredentials: true }),
        axios.get(`${API}/reminders/overdue`, { withCredentials: true }),
        axios.get(`${API}/reminders/upcoming?days=14`, { withCredentials: true }),
        axios.get(`${API}/reminders/missing-fields`, { withCredentials: true })
      ]);
      setSummary(summaryRes.data);
      setOverdue(overdueRes.data);
      setUpcoming(upcomingRes.data);
      setMissingFields(missingRes.data);
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-700';
      case 'high': return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700';
      case 'medium': return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700';
      default: return 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
    }
  };

  const getUrgencyStyles = (urgency) => {
    switch (urgency) {
      case 'urgent': return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
      case 'soon': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      default: return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };

  const getPriorityLevelStyles = (level) => {
    switch (level) {
      case 'critical': return 'from-rose-500 to-rose-600 text-white';
      case 'high': return 'from-amber-500 to-amber-600 text-white';
      case 'medium': return 'from-blue-500 to-blue-600 text-white';
      default: return 'from-emerald-500 to-emerald-600 text-white';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1 overflow-x-auto">
        {[
          { id: 'summary', label: 'Overview', icon: Bell },
          { id: 'overdue', label: `Overdue (${overdue?.total_count || 0})`, icon: AlertTriangle },
          { id: 'upcoming', label: `Upcoming (${upcoming?.total_count || 0})`, icon: Clock },
          { id: 'missing', label: `Missing (${missingFields?.total_count || 0})`, icon: FileWarning }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
            }`}
            data-testid={`reminders-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Summary Tab */}
      {activeTab === 'summary' && summary && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Priority Score */}
          <div className={`rounded-xl p-6 bg-gradient-to-br ${getPriorityLevelStyles(summary.priority_level)}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Priority Score</p>
                <p className="text-4xl font-bold mt-1">{summary.priority_score}</p>
                <p className="text-sm mt-1 capitalize opacity-90">{summary.priority_level} Priority</p>
              </div>
              <Bell className="w-16 h-16 opacity-30" />
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-rose-500" />
                <span className="text-xs text-slate-500 dark:text-slate-400">Overdue</span>
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.summary.overdue.count}</p>
              {summary.summary.overdue.critical > 0 && (
                <p className="text-xs text-rose-600 mt-1">{summary.summary.overdue.critical} critical</p>
              )}
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-slate-500 dark:text-slate-400">Upcoming</span>
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.summary.upcoming.count}</p>
              {summary.summary.upcoming.urgent > 0 && (
                <p className="text-xs text-amber-600 mt-1">{summary.summary.upcoming.urgent} urgent</p>
              )}
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileWarning className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-slate-500 dark:text-slate-400">Missing Fields</span>
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.summary.missing_fields.count}</p>
              {summary.summary.missing_fields.most_common && (
                <p className="text-xs text-blue-600 mt-1 truncate">{summary.summary.missing_fields.most_common}</p>
              )}
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-4 h-4 text-purple-500" />
                <span className="text-xs text-slate-500 dark:text-slate-400">Alerts</span>
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.summary.unread_alerts}</p>
              <p className="text-xs text-slate-500 mt-1">unread</p>
            </div>
          </div>

          {/* Top Actions */}
          {summary.top_actions?.length > 0 && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Top Priority Actions</h3>
              <div className="space-y-2">
                {summary.top_actions.map((action, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      action.priority === 1 ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' :
                      action.priority === 2 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                      'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                    }`}>
                      {action.priority}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{action.action}</p>
                      <p className="text-xs text-slate-500 capitalize">{action.type}</p>
                    </div>
                    {action.amount && (
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        ${action.amount.toLocaleString()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Overdue Tab */}
      {activeTab === 'overdue' && overdue && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Summary */}
          <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rose-700 dark:text-rose-300">Total Overdue</p>
                <p className="text-2xl font-bold text-rose-900 dark:text-rose-100">
                  ${overdue.total_amount.toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-rose-700 dark:text-rose-400">{overdue.total_count}</p>
                <p className="text-sm text-rose-600 dark:text-rose-400">invoices</p>
              </div>
            </div>
            {overdue.critical_count > 0 && (
              <p className="text-xs text-rose-600 dark:text-rose-400 mt-2">
                {overdue.critical_count} critically overdue (&gt;30 days)
              </p>
            )}
          </div>

          {/* Overdue List */}
          {overdue.overdue_invoices?.length > 0 ? (
            <div className="space-y-2">
              {overdue.overdue_invoices.map((inv) => (
                <div 
                  key={inv.invoice_id}
                  className={`p-4 rounded-xl border ${getSeverityStyles(inv.severity)}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${
                          inv.severity === 'critical' ? 'bg-rose-200 dark:bg-rose-800' :
                          inv.severity === 'high' ? 'bg-amber-200 dark:bg-amber-800' :
                          'bg-blue-200 dark:bg-blue-800'
                        }`}>
                          {inv.severity}
                        </span>
                        <span className="text-xs">{inv.days_overdue} days overdue</span>
                      </div>
                      <p className="font-medium mt-1">{inv.vendor}</p>
                      <p className="text-xs mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Due: {new Date(inv.due_date).toLocaleDateString()}
                      </p>
                    </div>
                    <p className="text-lg font-bold">${inv.amount?.toLocaleString() || '0'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-6 text-center">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-emerald-700 dark:text-emerald-300 font-medium">No overdue invoices!</p>
              <p className="text-emerald-600 dark:text-emerald-400 text-sm">All your payments are up to date.</p>
            </div>
          )}
        </motion.div>
      )}

      {/* Upcoming Tab */}
      {activeTab === 'upcoming' && upcoming && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Summary */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700 dark:text-amber-300">Due in {upcoming.days_range} Days</p>
                <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">
                  ${upcoming.total_amount.toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-amber-700 dark:text-amber-400">{upcoming.total_count}</p>
                <p className="text-sm text-amber-600 dark:text-amber-400">invoices</p>
              </div>
            </div>
            {upcoming.urgent_count > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                {upcoming.urgent_count} urgent (due in 2 days)
              </p>
            )}
          </div>

          {/* Upcoming List */}
          {upcoming.upcoming_invoices?.length > 0 ? (
            <div className="space-y-2">
              {upcoming.upcoming_invoices.map((inv) => (
                <div 
                  key={inv.invoice_id}
                  className={`p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${getUrgencyStyles(inv.urgency)}`}>
                          {inv.urgency}
                        </span>
                        <span className="text-xs text-slate-500">
                          {inv.days_until_due === 0 ? 'Due today' : 
                           inv.days_until_due === 1 ? 'Due tomorrow' : 
                           `Due in ${inv.days_until_due} days`}
                        </span>
                      </div>
                      <p className="font-medium text-slate-900 dark:text-white mt-1">{inv.vendor}</p>
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(inv.due_date).toLocaleDateString()}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-slate-900 dark:text-white">
                      ${inv.amount?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 text-center">
              <Timer className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 dark:text-slate-400">No upcoming due dates in the next {upcoming.days_range} days.</p>
            </div>
          )}
        </motion.div>
      )}

      {/* Missing Fields Tab */}
      {activeTab === 'missing' && missingFields && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Summary */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-blue-700 dark:text-blue-300">Invoices With Issues</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{missingFields.total_count}</p>
              </div>
              <FileQuestion className="w-10 h-10 text-blue-300" />
            </div>
            
            {/* Field Summary */}
            {Object.keys(missingFields.field_summary || {}).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(missingFields.field_summary).map(([field, count]) => (
                  <span 
                    key={field}
                    className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 text-xs rounded-full"
                  >
                    {field.replace('_', ' ')}: {count}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Missing Fields List */}
          {missingFields.invoices_with_issues?.length > 0 ? (
            <div className="space-y-2">
              {missingFields.invoices_with_issues.slice(0, 10).map((inv) => (
                <div 
                  key={inv.invoice_id}
                  className={`p-4 bg-white dark:bg-slate-900 rounded-xl border ${
                    inv.severity === 'high' 
                      ? 'border-rose-200 dark:border-rose-700' 
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          inv.severity === 'high' 
                            ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' 
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {inv.severity}
                        </span>
                        <span className="text-sm font-medium text-slate-900 dark:text-white">{inv.vendor}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {inv.missing_fields.map((field) => (
                          <span 
                            key={field}
                            className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs rounded"
                          >
                            {field.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                    {inv.amount && (
                      <p className="text-lg font-bold text-slate-900 dark:text-white">
                        ${inv.amount.toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              
              {missingFields.total_count > 10 && (
                <p className="text-center text-sm text-slate-500">
                  Showing 10 of {missingFields.total_count} invoices
                </p>
              )}
            </div>
          ) : (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-6 text-center">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-emerald-700 dark:text-emerald-300 font-medium">All invoices are complete!</p>
              <p className="text-emerald-600 dark:text-emerald-400 text-sm">No missing fields detected.</p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

export default RemindersPanel;
